import { useState, type ReactNode } from 'react'
import { FEATURES, type FeatureKey, type Features } from '../../types'
import { PLANS, planFor } from '../../plans'
import {
  BellRingIcon,
  ChartLineIcon,
  ChefHatIcon,
  ClipboardListIcon,
  LayersMinusIcon,
  HandCoinsIcon,
  HeartIcon,
  ImageIcon,
  NotebookPenIcon,
  QrCodeIcon,
  ScaleIcon,
  TabletIcon,
  UtensilsIcon,
  XIcon,
} from '../icons'
import { ColumnFilter } from './ColumnFilter'
import './FeatureTable.css'

const ICONS: Record<FeatureKey, ReactNode> = {
  cari: <NotebookPenIcon />,
  qr: <QrCodeIcon />,
  sadaka: <HandCoinsIcon />,
  takip: <ClipboardListIcon />,
  tarti: <ScaleIcon />,
  gorsel: <ImageIcon />,
  yemek: <UtensilsIcon />,
  mutfak: <ChefHatIcon />,
  stok: <LayersMinusIcon />,
  maliyet: <ChartLineIcon />,
  kiosk: <TabletIcon />,
  qrsiparis: <BellRingIcon />,
  damga: <HeartIcon />,
}

/** Paket sütunu: özelliği ilk açan paket, paket dışındakiler "ek". */
type Group = 'start' | 'plus' | 'max' | 'ek'
const GROUPS: { id: Group; name: string }[] = [
  ...PLANS.map((p) => ({ id: p.id, name: p.name })),
  { id: 'ek', name: 'Ek modül' },
]
const groupOf = (key: FeatureKey): Group => planFor(key)?.id ?? 'ek'
const ORDER = GROUPS.map((g) => g.id)

// Paket sırasıyla (Start, Plus, Max, Ek modül), paket içinde ada göre.
const ROWS = [...FEATURES].sort(
  (a, b) =>
    ORDER.indexOf(groupOf(a.key)) - ORDER.indexOf(groupOf(b.key)) || a.name.localeCompare(b.name, 'tr'),
)

const fold = (s: string) => s.toLocaleLowerCase('tr')

/**
 * Özellik tablosu (31 Eki 2026, kullanıcı: liste uzayacak, satır satır olsun,
 * her sütunda filtre). Satır başına bir özellik: ad ve açıklama, paketi,
 * açık/kapalı anahtarı. Sütun başlıklarının altında filtreler. Aktivasyon
 * kodu ve kurum düzenleme aynı bileşeni kullanıyor.
 */
export function FeatureTable({
  label,
  features,
  onChange,
}: {
  label: string
  features: Features
  onChange: (next: Features) => void
}) {
  const [query, setQuery] = useState('')
  const [group, setGroup] = useState<Group | ''>('')
  const [state, setState] = useState<'' | 'on' | 'off'>('')

  const q = fold(query.trim())
  const rows = ROWS.filter((f) => {
    const on = features[f.key] === true
    if (q && !fold(f.name).includes(q) && !fold(f.description).includes(q)) return false
    if (group && groupOf(f.key) !== group) return false
    if (state === 'on' && !on) return false
    if (state === 'off' && on) return false
    return true
  })
  const filtered = q !== '' || group !== '' || state !== ''
  const onCount = FEATURES.filter((f) => features[f.key] === true).length

  return (
    <div className="ftable">
      <div className="ftable__head">
        <span className="codes__features-label">{label}</span>
        <span className="ftable__count">
          {onCount}/{FEATURES.length} açık
        </span>
      </div>

      <div className="ftable__wrap">
        <table>
          <thead>
            <tr>
              <th>
                <ColumnFilter label="Özellik" query={query} onQuery={setQuery} placeholder="Özellik ara" />
              </th>
              <th className="ftable__col-plan">
                <ColumnFilter
                  label="Paket"
                  value={group}
                  onChange={(v) => setGroup(v as Group | '')}
                  options={GROUPS.map((g) => ({ value: g.id, label: g.name }))}
                />
              </th>
              <th className="ftable__col-state">
                <ColumnFilter
                  label="Durum"
                  value={state}
                  onChange={(v) => setState(v as '' | 'on' | 'off')}
                  options={[
                    { value: 'on', label: 'Açık' },
                    { value: 'off', label: 'Kapalı' },
                  ]}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => {
              const on = features[f.key] === true
              const g = groupOf(f.key)
              return (
                <tr key={f.key} className={on ? 'is-on' : ''}>
                  <td>
                    <div className="ftable__name">
                      <span className="ftable__icon">{ICONS[f.key]}</span>
                      {/* Açıklama yok (1 Kas 2026, kullanıcı: "ben biliyorum").
                          Arama yine açıklamada da arıyor. */}
                      <strong>{f.name}</strong>
                    </div>
                  </td>
                  <td className="ftable__col-plan">
                    <span className={`ftable__plan is-${g}`}>{GROUPS.find((x) => x.id === g)!.name}</span>
                  </td>
                  <td className="ftable__col-state">
                    <button
                      type="button"
                      className={`ftable__switch ${on ? 'is-on' : ''}`}
                      role="switch"
                      aria-checked={on}
                      aria-label={f.name}
                      onClick={() => onChange({ ...features, [f.key]: !on })}
                    >
                      <span />
                    </button>
                  </td>
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} className="ftable__empty">
                  Bu filtrelere uyan özellik yok.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {filtered && (
        <div className="ftable__foot">
          <span>
            {rows.length}/{FEATURES.length} özellik gösteriliyor
          </span>
          <button
            type="button"
            className="ftable__clear"
            onClick={() => {
              setQuery('')
              setGroup('')
              setState('')
            }}
          >
            <XIcon />
            Filtreleri temizle
          </button>
        </div>
      )}
    </div>
  )
}
